import Vue from "vue";
import VueI18n from "vue-i18n";

import customEN from "@/locales/lang/en";
import enLocale from "element-ui/lib/locale/lang/en";
import { localeMap } from "@/components/company-common-components/constants";
import { reloadOnceForChunkError } from "@/utils/chunk-reload";

Vue.use(VueI18n);

const DEFAULT_LOCALE = "en";

const localeLoaders = {
  "zh-cn": () =>
    Promise.all([
      import(
        /* webpackChunkName: "locale-zh-cn" */ "@/locales/lang/zh-cn"
      ),
      import(
        /* webpackChunkName: "locale-zh-cn" */ "element-ui/lib/locale/lang/zh-CN"
      ),
    ]),
  "zh-tw": () =>
    Promise.all([
      import(
        /* webpackChunkName: "locale-zh-tw" */ "@/locales/lang/zh-tw"
      ),
      import(
        /* webpackChunkName: "locale-zh-tw" */ "element-ui/lib/locale/lang/zh-TW"
      ),
    ]),
  ja: () =>
    Promise.all([
      import(/* webpackChunkName: "locale-ja" */ "@/locales/lang/ja"),
      import(
        /* webpackChunkName: "locale-ja" */ "element-ui/lib/locale/lang/ja"
      ),
    ]),
  hi: () =>
    Promise.all([
      import(/* webpackChunkName: "locale-hi" */ "@/locales/lang/hi"),
    ]),
  ko: () =>
    Promise.all([
      import(/* webpackChunkName: "locale-ko" */ "@/locales/lang/ko"),
      import(
        /* webpackChunkName: "locale-ko" */ "element-ui/lib/locale/lang/ko"
      ),
    ]),
};

const i18n = new VueI18n({
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  silentFallbackWarn: true,
  messages: {
    [DEFAULT_LOCALE]: {
      ...enLocale,
      ...customEN,
    },
  },
});

const loadedLocales = new Set([DEFAULT_LOCALE]);

export function getStoredLocale() {
  try {
    const stored = localStorage.getItem("language");
    if (!stored) return DEFAULT_LOCALE;
    return localeMap.get(JSON.parse(stored)?.code) || DEFAULT_LOCALE;
  } catch (error) {
    return DEFAULT_LOCALE;
  }
}

export async function setI18nLocale(locale) {
  const nextLocale = localeLoaders[locale] ? locale : DEFAULT_LOCALE;

  if (!loadedLocales.has(nextLocale)) {
    let customModule;
    let elementModule;
    try {
      [customModule, elementModule] = await localeLoaders[nextLocale]();
    } catch (error) {
      reloadOnceForChunkError(error);
      throw error;
    }
    i18n.setLocaleMessage(nextLocale, {
      ...(elementModule?.default || {}),
      ...customModule.default,
    });
    loadedLocales.add(nextLocale);
  }

  i18n.locale = nextLocale;
  return nextLocale;
}

export async function loadStoredLocale() {
  try {
    return await setI18nLocale(getStoredLocale());
  } catch (error) {
    i18n.locale = DEFAULT_LOCALE;
    return DEFAULT_LOCALE;
  }
}

export default i18n;
